import {
  DateTimeOffset,
  MappedReservedTimes,
} from 'components/Form/BDatePicker';
import { Dayjs } from 'dayjs';
import { FormAction } from 'hooks/useForm';
import { Dispatch, useEffect, useRef } from 'react';
import { dateService } from 'services/DateService';
import {
  DemandResponseDemand,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { usePriceWidgetHourGenerator } from './usePriceWidgetHourGenerator';
import { useQueryParams } from 'hooks/useQueryParams';

interface CalculatedDateMap {
  date: Date;
  isSwimmingTour: boolean;
  changedToDate?: Date;
}

export function usePriceWidgetClosestAvailability(
  state: VehicleBasedDemandState,
  dispatch: Dispatch<FormAction<VehicleBasedDemandState>>,
  mappedReservedTimes: MappedReservedTimes,
  datePickerDisabledHours: DateTimeOffset[],
  demandData?: DemandResponseDemand
) {
  const { generateHourArray } = usePriceWidgetHourGenerator();
  const calculatedDatesMap = useRef<CalculatedDateMap[]>([]);
  const dayjs = dateService.getDayjs();
  const { getQueryParam } = useQueryParams();
  const isFindFirstAvailableHourFirstLoop = useRef<boolean>(true);
  const morningHours = {
    start: 8,
    end: 18,
  };

  const constructSchedule = (date: Date) => {
    const formattedDate = dateService.getFormattedDate(date);

    const currentReservedTimes = [
      ...(mappedReservedTimes[formattedDate] ?? []),
    ];

    for (const offsetTime of datePickerDisabledHours) {
      let intersectedReservedTime = currentReservedTimes.find(
        (time) => time.hour === offsetTime.hour
      );
      if (intersectedReservedTime) {
        intersectedReservedTime = {
          hour: intersectedReservedTime.hour,
          harborId: intersectedReservedTime.harborId,
          isOffset: offsetTime.isOffset && intersectedReservedTime.isOffset,
        };
      } else {
        currentReservedTimes.push({ ...offsetTime });
      }
    }

    return [...currentReservedTimes].sort((a, b) => a.hour - b.hour);
  };

  const findFirstUnavailableHour = (
    schedule: DateTimeOffset[],
    startHour: number
  ) => {
    return schedule
      .sort((a, b) => a.hour - b.hour)
      .find((d) => d.hour > startHour && !d.isOffset);
  };

  const checkIfDateAvailable = (
    hour: number,
    date: Dayjs,
    schedule: DateTimeOffset[],
    dayOffset: number
  ) => {
    const fromQueryParam = getQueryParam('date')
      ? date.isSame(dayjs(Number(getQueryParam('date'))), 'hour')
      : false;
    const fromDemandData = demandData?.startTime
      ? date.isSame(dayjs(demandData.startTime), 'hour')
      : false;
    const isFirstLoop = isFindFirstAvailableHourFirstLoop.current;
    const disabledHoursIfFirstLoop =
      dayOffset < 7 &&
      isFirstLoop &&
      !fromQueryParam &&
      !fromDemandData &&
      (hour < morningHours.start || hour >= morningHours.end);
    if (disabledHoursIfFirstLoop) return false;
    const isNotInSchedule = schedule.every((h) => h.hour !== hour);
    const isLaterThanToday =
      date.hour(hour).toDate().getTime() > new Date().getTime();
    return isNotInSchedule && isLaterThanToday;
  };

  const isDateAvailable = (date: Date) => {
    const schedule = constructSchedule(date);
    const dayjsDate = dayjs(date);
    const hour = dayjsDate.hour();
    return checkIfDateAvailable(hour, dayjsDate, schedule, 0);
  };

  const findFirstAvailableHour = (date: Date, isSwimmingTour: boolean) => {
    const hourArray = generateHourArray();
    let findedClosestAvailability: Date | null = null;
    let currentDayOffset = 0;
    while (findedClosestAvailability === null) {
      const toBeCalculatedDate = dayjs(date)
        .minute(0)
        .add(currentDayOffset, 'day');
      const toBeCalculatedDateHour = toBeCalculatedDate.hour();
      const schedule = constructSchedule(toBeCalculatedDate.toDate());
      if (isSwimmingTour) {
        if (
          checkIfDateAvailable(
            10,
            toBeCalculatedDate,
            schedule,
            currentDayOffset
          )
        ) {
          return toBeCalculatedDate.hour(10).toDate();
        }
      } else {
        if (
          checkIfDateAvailable(
            toBeCalculatedDateHour,
            toBeCalculatedDate,
            schedule,
            currentDayOffset
          )
        ) {
          return toBeCalculatedDate.toDate();
        }

        const startOfToBeCalculatedDate = toBeCalculatedDate.startOf('day');
        const availableHour = hourArray.find((hour) => {
          return checkIfDateAvailable(
            hour,
            startOfToBeCalculatedDate,
            schedule,
            currentDayOffset
          );
        });
        findedClosestAvailability =
          availableHour !== undefined
            ? toBeCalculatedDate.hour(availableHour).toDate()
            : null;
      }

      currentDayOffset++;
      if (currentDayOffset > 5 * 30) {
        break;
      }
    }
    return findedClosestAvailability;
  };

  useEffect(() => {
    if (state.date) {
      const generateMap: CalculatedDateMap = {
        date: state.date,
        isSwimmingTour: state.isSwimmingTour,
      };
      const isCalculated = calculatedDatesMap.current.find(
        (item) =>
          item.date.getTime() === generateMap.date.getTime() &&
          item.isSwimmingTour === generateMap.isSwimmingTour
      );
      if (!isCalculated) {
        const findedFirstAvailableDate = findFirstAvailableHour(
          state.date,
          state.isSwimmingTour
        );
        isFindFirstAvailableHourFirstLoop.current = false;
        if (
          findedFirstAvailableDate &&
          findedFirstAvailableDate.getTime() !== state.date.getTime()
        ) {
          dispatch({
            type: 'date',
            payload: findedFirstAvailableDate,
          });
          generateMap.changedToDate = findedFirstAvailableDate;
        }
        calculatedDatesMap.current.push(generateMap);
      } else if (isCalculated.changedToDate) {
        dispatch({
          type: 'date',
          payload: isCalculated.changedToDate,
        });
      }
    }
  }, [state.isSwimmingTour, mappedReservedTimes, datePickerDisabledHours]);

  return {
    constructSchedule,
    findFirstUnavailableHour,
    findFirstAvailableHour,
    isDateAvailable,
  };
}
